import React, { useState, useEffect, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import MenuBar from './Component/MenuBar';
import Sidebar from './Component/Sidebar';
import Footer from './Component/Footer';
import { wordsOfWisdomAPI } from '../services/api';
import './WordsOfWisdomAdmin.css';

const WordsOfWisdomAdmin = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // Load all entries from the database
  const loadEmails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await wordsOfWisdomAPI.getAll();
      if (result.success !== false) {
        setEmails(Array.isArray(result) ? result : result.data || []);
      } else {
        setError(result.message || 'Failed to load Words of Wisdom');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEmails();
  }, [loadEmails]);

  // Load single email detail
  const loadEmailDetail = useCallback(async (id) => {
    try {
      setLoadingDetail(true);
      setError(null);
      const result = await wordsOfWisdomAPI.getById(id);
      const emailData = Array.isArray(result) ? result[0] : (result.data || result);
      if (emailData) {
        setSelectedEmail(emailData);
      } else {
        setError('Email not found');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  // Trigger Gmail sync
  const handleSync = useCallback(async (fullSync = false) => {
    try {
      setSyncing(true);
      setError(null);
      setSyncResult(null);
      setSuccessMsg(null);

      const result = await wordsOfWisdomAPI.syncGmail({ fullSync });

      if (result.success) {
        setSyncResult(result);
        setSuccessMsg(
          `Sync complete — Found: ${result.found}, Imported: ${result.imported}, Skipped: ${result.skipped}` +
          (result.failed > 0 ? `, Failed: ${result.failed}` : '')
        );
        // Reload the table
        await loadEmails();
      } else {
        setError(result.message || 'Sync failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  }, [loadEmails]);

  // Format date for table display
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  // Format date for detail view
  const formatEmailDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  // Get the email body content for the detail view
  const getBodyContent = (email) => {
    if (email?.htmlBody && email.htmlBody.trim().length > 0) {
      return email.htmlBody;
    }
    if (email?.textBody && email.textBody.trim().length > 0) {
      return email.textBody.replace(/\n/g, '<br/>');
    }
    return '<p>No content available.</p>';
  };

  // Determine source label
  const getSourceLabel = (email) => {
    if (email.gmailMessageId || email.Gmail_Message_Id) {
      return 'Gmail';
    }
    return 'Manual';
  };

  return (
    <>
      <MenuBar />
      <Sidebar />

      {/* Page Banner */}
      <section className="page-banner">
        <div className="image-layer"></div>
        <div className="auto-container">
          <h1>Words of Wisdom — Admin</h1>
        </div>
        <div className="breadcrumb-box">
          <div className="auto-container">
            <ul className="bread-crumb clearfix">
              <li><NavLink to="/USA/Home">Home</NavLink></li>
              <li><NavLink to="/USA/wordsofwisdom">Words of Wisdom</NavLink></li>
              <li className="active">Admin</li>
            </ul>
          </div>
        </div>
      </section>

      <div className="wow-admin-page">
        <div className="wow-admin-container">

          {/* Error message */}
          {error && (
            <div className="wow-admin-alert wow-admin-alert--error">
              <i className="fas fa-exclamation-circle"></i>
              <span>{error}</span>
              <button onClick={() => setError(null)}>&#10005;</button>
            </div>
          )}

          {/* Success message */}
          {successMsg && (
            <div className="wow-admin-alert wow-admin-alert--success">
              <i className="fas fa-check-circle"></i>
              <span>{successMsg}</span>
              <button onClick={() => setSuccessMsg(null)}>&#10005;</button>
            </div>
          )}

          {/* Detail View */}
          {selectedEmail && !loadingDetail && (
            <div className="wow-admin-detail">
              <button
                className="wow-admin-back-btn"
                onClick={() => setSelectedEmail(null)}
              >
                <i className="fas fa-arrow-left"></i> Back to List
              </button>

              <div className="wow-admin-detail__header">
                <h2>{selectedEmail.subject}</h2>
                <div className="wow-admin-detail__meta">
                  <span><strong>Sender:</strong> {selectedEmail.senderName || selectedEmail.Sender_Name || 'Radha Madhav Society'}</span>
                  <span><strong>Email:</strong> {selectedEmail.senderEmail || selectedEmail.Sender_Email || '—'}</span>
                  <span><strong>Date:</strong> {formatEmailDate(selectedEmail.emailDate || selectedEmail.Email_Date)}</span>
                  <span><strong>Source:</strong> {getSourceLabel(selectedEmail)}</span>
                  <span><strong>Email Sent:</strong> {selectedEmail.isEmailSent || selectedEmail.Is_Email_Sent ? 'Yes' : 'No'}</span>
                  {(selectedEmail.emailSentDate || selectedEmail.Email_Sent_Date) && (
                    <span><strong>Sent Date:</strong> {formatEmailDate(selectedEmail.emailSentDate || selectedEmail.Email_Sent_Date)}</span>
                  )}
                </div>
              </div>

              <div className="wow-admin-detail__body">
                <div
                  className="wow-admin-email-content"
                  dangerouslySetInnerHTML={{ __html: getBodyContent(selectedEmail) }}
                />
              </div>
            </div>
          )}

          {/* Loading detail spinner */}
          {loadingDetail && (
            <div className="wow-admin-loading">
              <div className="wow-admin-spinner"></div>
              <p>Loading email details...</p>
            </div>
          )}

          {/* Table View */}
          {!selectedEmail && !loadingDetail && (
            <>
              {/* Action bar */}
              <div className="wow-admin-actions">
                <h2 className="wow-admin-title">
                  <i className="fas fa-envelope-open-text"></i> Words of Wisdom
                </h2>
                <div className="wow-admin-actions__buttons">
                  <button
                    className="wow-admin-btn wow-admin-btn--sync"
                    onClick={() => handleSync(false)}
                    disabled={syncing}
                  >
                    <i className={syncing ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'}></i>
                    {syncing ? ' Syncing...' : ' Sync Gmail'}
                  </button>
                  <button
                    className="wow-admin-btn wow-admin-btn--full-sync"
                    onClick={() => handleSync(true)}
                    disabled={syncing}
                    title="Fetch all historical emails from Gmail"
                  >
                    <i className={syncing ? 'fas fa-spinner fa-spin' : 'fas fa-history'}></i>
                    {syncing ? ' Syncing...' : ' Full Sync'}
                  </button>
                  <button
                    className="wow-admin-btn wow-admin-btn--refresh"
                    onClick={loadEmails}
                    disabled={loading}
                  >
                    <i className="fas fa-redo"></i> Refresh
                  </button>
                </div>
              </div>

              {/* Sync result summary */}
              {syncResult && (
                <div className="wow-admin-sync-result">
                  <span><strong>Found:</strong> {syncResult.found}</span>
                  <span><strong>Imported:</strong> {syncResult.imported}</span>
                  <span><strong>Skipped:</strong> {syncResult.skipped}</span>
                  {syncResult.failed > 0 && (
                    <span className="wow-admin-sync-result--failed">
                      <strong>Failed:</strong> {syncResult.failed}
                    </span>
                  )}
                </div>
              )}

              {/* Loading state */}
              {loading && (
                <div className="wow-admin-loading">
                  <div className="wow-admin-spinner"></div>
                  <p>Loading...</p>
                </div>
              )}

              {/* Table */}
              {!loading && (
                <>
                  <div className="wow-admin-count">
                    Showing {emails.length} entr{emails.length !== 1 ? 'ies' : 'y'}
                  </div>

                  {emails.length === 0 ? (
                    <div className="wow-admin-empty">
                      <i className="fas fa-inbox"></i>
                      <p>No Words of Wisdom entries found. Click "Sync Gmail" to import emails.</p>
                    </div>
                  ) : (
                    <div className="wow-admin-table-wrapper">
                      <table className="wow-admin-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Subject</th>
                            <th>Sender</th>
                            <th>Date</th>
                            <th>Email Sent</th>
                            <th>Sent Date</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {emails.map((email, index) => (
                            <tr key={email.wordsOfWisdomId || email.Words_Of_Wisdom_Id}>
                              <td>{index + 1}</td>
                              <td className="wow-admin-table__subject">
                                {email.subject || email.Subject}
                              </td>
                              <td>{email.senderName || email.Sender_Name || 'Radha Madhav Society'}</td>
                              <td>{formatDate(email.emailDate || email.Email_Date)}</td>
                              <td>
                                <span className={
                                  (email.isEmailSent || email.Is_Email_Sent)
                                    ? 'wow-admin-badge wow-admin-badge--sent'
                                    : 'wow-admin-badge wow-admin-badge--pending'
                                }>
                                  {(email.isEmailSent || email.Is_Email_Sent) ? 'Yes' : 'No'}
                                </span>
                              </td>
                              <td>{formatDate(email.emailSentDate || email.Email_Sent_Date)}</td>
                              <td>
                                <button
                                  className="wow-admin-btn wow-admin-btn--view"
                                  onClick={() => loadEmailDetail(email.wordsOfWisdomId || email.Words_Of_Wisdom_Id)}
                                >
                                  <i className="fas fa-eye"></i> View
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </>
          )}

        </div>
      </div>

      <Footer />
    </>
  );
};

export default WordsOfWisdomAdmin;
